import React, { useState, useEffect, useRef } from 'react';
import { Route, Redirect } from 'react-router-dom';

import {
  isAuthenticated,
  getData,
  getAcessos,
  acessosEstaoStale,
  logout,
} from '../../services/auth';
import { atualizaAcessos } from '../../services/acessos';
import { toast } from 'react-toastify';

const trataUrl = props => {
  const teste2 = props.match.path.split('/:');
  return teste2[0];
};

// Aviso disparado via setTimeout com dedupe em escopo de módulo: o <Redirect>
// troca a rota no componentDidMount e desmonta este componente antes de um
// useEffect rodar (effect de componente desmontado é cancelado), e toast
// direto no render é side-effect que o React proíbe (era a fonte de spam).
let avisoAgendado = false;
const avisaNaoAutorizado = () => {
  if (avisoAgendado) return;
  avisoAgendado = true;
  setTimeout(() => {
    avisoAgendado = false;
    toast.error('Rota não Autorizada');
  }, 300);
};

// O Redirect NÃO leva state.from: o SignIn autenticado faz history.push(from),
// o que devolveria o usuário à rota barrada e fecharia um loop infinito de
// redirects (Maximum update depth / React error #185). Sem from, o SignIn
// manda pra /app — rota que o SessionController injeta no JWT de todo usuário.
const RedirectNaoAutorizado = () => {
  avisaNaoAutorizado();
  return <Redirect to="/" />;
};

const PrivateRoute = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    render={props => <RotaPrivada Component={Component} {...props} />}
  />
);

// #133: a permissão deixou de sair só do JWT (que valia 1 dia) e passa a vir de
// uma cópia local revalidada contra GET /meus-acessos.
//
// A decisão é assimétrica de propósito: PERMITIR é otimista (renderiza já e
// revalida em segundo plano), NEGAR é pessimista (busca a lista antes de
// barrar). Assim quem acabou de receber acesso entra na primeira navegação, e
// as navegações comuns não piscam. Negar é a decisão que exige informação
// fresca; permitir, não.
const RotaPrivada = ({ Component, ...props }) => {
  // getData() faz jwtDecode(token) e LANÇA InvalidTokenError com token
  // nulo/corrompido (usuário deslogado) — sem o try/catch o Route crasha a
  // árvore inteira em vez de mandar pro login.
  let userData = null;
  try {
    userData = getData();
  } catch (e) {
    userData = null;
  }

  const rota = trataUrl(props);
  const [acessos, setAcessos] = useState(() => getAcessos());
  // Qual rota já teve a negação CONFIRMADA contra o servidor. Um booleano
  // "revalidando" não serve: ele só poderia ser ligado dentro do efeito, que
  // roda depois do render — e o <Redirect> age no componentDidMount, antes
  // disso, então a primeira renderização de uma rota negada pela cópia local
  // sempre venceria a corrida e barraria antes da resposta do servidor
  // chegar. Guardando a rota confirmada, a própria renderização já sabe que
  // ainda não confirmou nada.
  const [rotaConfirmada, setRotaConfirmada] = useState(null);

  const temAcesso = acessos.some(a => a && a.rota === rota);
  const negacaoConfirmada = rotaConfirmada === rota;

  // O efeito de visibilitychange abaixo é montado uma única vez ([] de
  // dependências — ver comentário lá) e este componente é reaproveitado
  // entre rotas-irmãs sem `key`, então uma closure sobre `rota` ficaria
  // presa à rota da primeira montagem. O ref é atualizado a cada render e
  // lido dentro do handler, sempre com a rota exibida no momento do evento.
  const rotaAtualRef = useRef(rota);
  rotaAtualRef.current = rota;

  useEffect(() => {
    let vivo = true;

    // Otimista: já pode entrar. Só atualiza em segundo plano se estiver velha.
    if (temAcesso) {
      if (acessosEstaoStale()) {
        atualizaAcessos().then(lista => {
          if (!vivo || !lista) return;
          setAcessos(lista);
          // Confirma a rota também aqui: a lista veio do servidor, então a
          // decisão está confirmada para esta rota — mesmo que ela tenha
          // sido revogada. Sem isto, uma revogação em segundo plano zera
          // temAcesso mas rotaConfirmada continua null, e o componente cai
          // no `return null` de "ainda não confirmei" para sempre — tela em
          // branco permanente, sem redirect e sem logout.
          setRotaConfirmada(rota);
        });
      }
      return () => {
        vivo = false;
      };
    }

    // Pessimista: confirma com o servidor antes de barrar.
    atualizaAcessos().then(lista => {
      if (!vivo) return;
      if (lista) setAcessos(lista);
      // Confirmado mesmo quando a busca falha (lista null): decidimos com o
      // que temos, em vez de deixar o usuário preso num loader para sempre.
      setRotaConfirmada(rota);
    });

    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rota]);

  // Aba deixada aberta o dia todo nunca troca de rota, então nunca reavaliaria.
  // Dependências [] de propósito: registrar/remover o listener a cada
  // navegação seria desperdício. A rota corrente vem do ref acima.
  useEffect(() => {
    let vivo = true;
    const aoVoltarPraAba = () => {
      if (document.visibilityState !== 'visible') return;
      if (!acessosEstaoStale()) return;
      atualizaAcessos().then(lista => {
        if (!vivo || !lista) return;
        setAcessos(lista);
        // Mesma correção do ramo otimista: a lista veio do servidor, então a
        // rota exibida agora (não a da montagem do efeito) está confirmada —
        // senão uma revogação detectada aqui também travaria em tela branca.
        setRotaConfirmada(rotaAtualRef.current);
      });
    };
    document.addEventListener('visibilitychange', aoVoltarPraAba);
    return () => {
      vivo = false;
      document.removeEventListener('visibilitychange', aoVoltarPraAba);
    };
  }, []);

  if (!userData) {
    return <Redirect to="/" />;
  }

  if (!isAuthenticated()) {
    return <Redirect to={{ pathname: '/', state: { from: props.location } }} />;
  }

  // Lista vazia = usuário inativo (exonerado). Todo usuário ativo tem ao
  // menos /app. Só desloga depois que a negação foi confirmada pelo servidor
  // — lista vazia nunca tem temAcesso true, então sem o gate de
  // negacaoConfirmada isto dispararia no primeiro render, antes de qualquer
  // resposta do servidor (o mesmo bug do guard antigo em revalidando).
  if (acessos.length === 0 && negacaoConfirmada) {
    logout();
    return <Redirect to="/" />;
  }

  // Negar /app com a lista já confirmada significa sessão sem home:
  // redirecionar fecharia o vaivém "/" -> SignIn -> /app -> nega -> "/".
  if (!temAcesso && negacaoConfirmada && rota === '/app') {
    logout();
    return <Redirect to="/" />;
  }

  if (!temAcesso && !negacaoConfirmada) {
    // Ainda não confirmamos com o servidor: não barre e não renderize a tela.
    return null;
  }

  if (!temAcesso) {
    return <RedirectNaoAutorizado />;
  }

  return <Component {...props} />;
};

export default PrivateRoute;
